import useSWR from 'swr';
import api from '../../services/api';

export interface ClientContact {
  clientContactId: number;
  clientId?: number;
  clientContactFirstName?: string;
  clientContactLastName?: string;
  clientContactPosition?: string;
  clientContactEmail?: string;
  clientContactPhone?: string;
  clientContactNotes?: string;
  clientContactCreatedAt?: string;
  clientContactCreatedBy?: number;
  clientContactUpdatedAt?: string;
  clientContactUpdatedBy?: number;
  client?: {
    clientName?: string;
    clientVATNumber?: string;
  };
}

export interface CreateClientContactData {
  clientId?: number;
  clientContactFirstName?: string;
  clientContactLastName?: string;
  clientContactPosition?: string;
  clientContactEmail?: string;
  clientContactPhone?: string;
  clientContactNotes?: string;
}

export const useClientContacts = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<ClientContact[]>(
    `/client-contacts${queryString}`,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    mutate,
  };
};

export const useClientContact = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<ClientContact>(
    id ? `/client-contacts/${id}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClientContactsByClient = (clientId: number) => {
  const { data, error, isLoading, mutate } = useSWR<ClientContact[]>(
    clientId ? `/client-contacts/all_of/${clientId}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const createClientContact = async (data: CreateClientContactData): Promise<ClientContact> => {
  const response = await api.post('/client-contacts', data);
  return response.data;
};

export const updateClientContact = async (id: number, data: Partial<CreateClientContactData>): Promise<ClientContact> => {
  const response = await api.patch(`/client-contacts/${id}`, data);
  return response.data;
};

export const deleteClientContact = async (id: number): Promise<void> => {
  await api.delete(`/client-contacts/${id}`);
};
